import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { ExternalLink, MapPin, Tag, Leaf } from 'lucide-react';
import { classify, cardImage, heroImage } from '../data/feedImages';
import { useAuth } from '../auth/AuthContext';

type ContentItem = {
  id: string; title: string; sourceUrl: string; summary: string;
  tags: { id: string; name: string }[]; province?: string | null; city?: string | null;
};
type TagObj = { id: string; name: string };

type Highlights = {
  lastCrawl: { province: string | null; pagesRelevant: number; finishedAt: string | null } | null;
  totals: { last7: number; last30: number };
  lastScrapeTheme: string | null;
  kb: { totalArticles: number; provinceCount: number; themeCount: number };
};

const PAGE = 30;

const timeAgo = (iso?: string | null): string => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const ACTION_BTN_BASE = 'py-[8px] rounded-[10px] text-[12px] font-medium cursor-pointer flex items-center justify-center gap-[5px] transition-all duration-150 border';
const actionBtnClass = (variant: 'blue' | 'ghost'): string => {
  const map = {
    blue:  'flex-1 bg-[rgba(56,189,248,0.08)] border-[rgba(56,189,248,0.2)] text-[color:var(--accent-blue)]',
    ghost: 'bg-[var(--glass-border)] border-[var(--glass-border)] text-[color:var(--text-muted)]',
  } as const;
  return `${ACTION_BTN_BASE} ${map[variant]}`;
};

const ModernUpdateFeed: React.FC = () => {
  // Members can browse the feed but not retag its items (the API rejects them too).
  const { isMember } = useAuth();
  const canEditTags = !isMember;
  const [items, setItems]           = useState<ContentItem[]>([]);
  const [total, setTotal]           = useState(0);
  const [tags, setTags]             = useState<TagObj[]>([]);
  const [provinces, setProvinces]   = useState<string[]>([]);
  const [province, setProvince]     = useState('all');
  const [loading, setLoading]       = useState(true);     // initial / province-change load
  const [loadingMore, setLoadingMore] = useState(false);
  const [editingFor, setEditingFor] = useState<string | null>(null);
  const [hl, setHl]                 = useState<Highlights | null>(null);

  // Refs to avoid stale closures inside the IntersectionObserver callback.
  const skipRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const provinceRef = useRef('all');

  const fetchPage = useCallback(async (reset: boolean) => {
    if (loadingRef.current) return;
    if (!reset && !hasMoreRef.current) return;
    loadingRef.current = true;
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const skip = reset ? 0 : skipRef.current;
      const r = await axios.get('/api/feed/items', { params: { province: provinceRef.current, skip, take: PAGE } });
      const { items: pageItems, total: t, hasMore } = r.data as { items: ContentItem[]; total: number; hasMore: boolean };
      hasMoreRef.current = hasMore;
      skipRef.current = skip + pageItems.length;
      setTotal(t);
      setItems(prev => (reset ? pageItems : [...prev, ...pageItems]));
    } catch (e) { console.error(e); }
    finally { loadingRef.current = false; setLoading(false); setLoadingMore(false); }
  }, []);

  // Static data once.
  useEffect(() => {
    axios.get('/api/tags').then(r => setTags(r.data)).catch(console.error);
    axios.get('/api/library/provinces').then(r => setProvinces(r.data)).catch(console.error);
    axios.get('/api/feed/highlights').then(r => setHl(r.data)).catch(console.error);
  }, []);

  // Reset + load first page when the province filter changes.
  useEffect(() => {
    provinceRef.current = province;
    fetchPage(true);
  }, [province, fetchPage]);

  // Infinite scroll: load the next page when the sentinel nears the viewport.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => { if (entries[0].isIntersecting) fetchPage(false); }, { rootMargin: '500px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchPage]);

  const updateTags = (id: string, selected: TagObj[]) =>
    axios.post(`/api/${id}/tags`, { tags: selected })
      .then(() => { setItems(p => p.map(i => i.id === id ? { ...i, tags: selected } : i)); setEditingFor(null); })
      .catch(console.error);

  // Hero cards (driven by /api/feed/highlights). Imagery rotates daily from a shared pool.
  const lc = hl?.lastCrawl;
  const theme = hl?.lastScrapeTheme;
  const kb = hl?.kb;
  const heroRotation = Math.floor(Date.now() / 86400000) % 12;
  const heroCards = [
    {
      eyebrow: 'Latest Crawl',
      big: lc ? `${lc.pagesRelevant} new` : `${total}`,
      sub: lc ? `${lc.province ?? 'crawl'}${lc.finishedAt ? ` · ${timeAgo(lc.finishedAt)}` : ''}` : 'articles curated',
      desc: hl ? `${hl.totals.last7} added in the last 7 days · ${hl.totals.last30} this month` : 'Loading latest crawl…',
      accent: classify(theme ?? '').accent,
    },
    {
      eyebrow: 'Top Theme',
      big: theme ?? (hl ? 'Mixed' : '…'),
      sub: 'latest crawl focus',
      desc: theme ? `Most of the latest crawl's articles focus on ${theme.toLowerCase()}.` : (hl ? 'The latest crawl spanned a mix of topics.' : 'Loading theme…'),
      accent: classify(theme ?? 'policy').accent,
    },
    {
      eyebrow: 'Knowledge Base',
      big: kb ? `${kb.totalArticles} articles` : '…',
      sub: 'curated & searchable',
      desc: kb ? `across ${kb.provinceCount} province${kb.provinceCount === 1 ? '' : 's'}, ${kb.themeCount} theme${kb.themeCount === 1 ? '' : 's'}` : 'Loading…',
      accent: 'var(--accent)',
    },
  ];

  return (
    <div className="bg-[var(--bg)] min-h-full">

      {/* ── Nature hero ── */}
      <div className="relative overflow-hidden bg-[linear-gradient(180deg,var(--accent-dim)_0%,var(--bg)_100%)] px-[32px] pt-[32px] pb-0">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_50%,var(--accent-dim)_0%,transparent_60%),radial-gradient(circle_at_80%_20%,rgba(56,189,248,0.04)_0%,transparent_50%)]" />

        <div className="relative z-[1]">
          <h1 className="flex items-center gap-[10px] text-[24px] font-bold text-[color:var(--text-primary)] mt-0 mb-[6px] mx-0">
            <Leaf size={22} color="var(--accent)" />
            Content Feed
          </h1>
          <p className="text-[14px] text-[color:var(--accent-strong)] mt-0 mb-[28px] mx-0">
            {kb?.totalArticles ?? total} relevant articles · {provinces.length} provinces · {tags.length} topics
          </p>

          {/* 3 dynamic hero cards — unique image each, rotating daily from a shared pool */}
          <div className="grid grid-cols-3 gap-[16px] mb-[28px]">
            {heroCards.map((card, i) => (
              <div key={i} className="relative rounded-[16px] overflow-hidden h-[180px] cursor-default">
                <img
                  src={heroImage(i, heroRotation)}
                  alt={card.eyebrow}
                  loading="lazy"
                  onError={e => { const t = e.currentTarget; if (!t.dataset.fb) { t.dataset.fb = '1'; t.src = 'https://loremflickr.com/600/360/nature?lock=99'; } }}
                  className="absolute inset-0 w-full h-full object-cover [filter:brightness(0.45)_saturate(1.1)]"
                />
                <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(0,0,0,0.1)_0%,rgba(0,0,0,0.62)_100%)]" />
                <div className="relative z-[1] p-[20px] h-full flex flex-col justify-end">
                  <p className="text-[10px] font-semibold tracking-[0.1em] uppercase mb-[6px]" style={{ color: card.accent }}>{card.eyebrow}</p>
                  <h3 className="text-[18px] font-bold text-[#fafafa] mt-0 mb-[6px] mx-0 leading-[1.2]">
                    {card.big}
                    {card.sub && <span className="block text-[13px] font-normal text-[color:var(--text-secondary)] mt-[2px]">{card.sub}</span>}
                  </h3>
                  <p className="text-[12px] text-[rgba(255,255,255,0.82)] leading-[1.5] m-0">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="flex items-center gap-[12px] px-[32px] py-[20px] bg-[var(--surface)] border-b border-[var(--glass-border)] flex-wrap">
        <div className="relative">
          <select value={province} onChange={e => setProvince(e.target.value)} className="appearance-none bg-[var(--glass-border)] border border-[var(--glass-border)] rounded-[10px] pl-[12px] pr-[32px] py-[7px] text-[13px] text-[color:var(--text-secondary)] cursor-pointer outline-none">
            <option value="all">All Provinces</option>
            {provinces.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <MapPin size={12} color="var(--text-muted)" className="absolute right-[10px] top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <span className="text-[13px] text-[color:var(--border-subtle)] ml-auto">{items.length} of {total} items</span>
      </div>

      {/* ── Content ── */}
      {loading && (
        <div className="flex justify-center items-center py-[80px] gap-[8px]">
          {[0,150,300].map(d => <div key={d} className="w-[8px] h-[8px] rounded-full bg-[var(--accent)]" style={{ animation:`bounce 1s ${d}ms infinite` }} />)}
        </div>
      )}
      {!loading && items.length === 0 && <div className="text-center py-[80px] text-[color:var(--border-subtle)] text-[14px]">No content found.</div>}

      {!loading && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(420px,1fr))] gap-[16px] px-[32px] py-[24px]">
          {items.map(item => {
            const meta = cardImage([...item.tags.map(t => t.name), item.title, item.summary].join(' '), item.id);
            return (
              <div key={item.id} className="bg-[var(--surface)] border border-[var(--glass-border)] rounded-[16px] overflow-hidden flex flex-col [transition:border-color_0.2s,transform_0.2s]"
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${meta.accent}30`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--glass-border)'; (e.currentTarget as HTMLElement).style.transform = 'none'; }}
              >
                <div className="relative h-[120px] overflow-hidden">
                  <img
                    src={meta.url}
                    alt={meta.label}
                    loading="lazy"
                    onError={e => { const t = e.currentTarget; if (t.src !== meta.fallback) t.src = meta.fallback; }}
                    className="w-full h-full object-cover [filter:brightness(0.5)_saturate(1.1)] [transition:transform_0.4s_ease]"
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-[60px] bg-[linear-gradient(to_bottom,transparent,var(--surface))]" />
                  <div className="absolute top-[10px] left-[10px] px-[10px] py-[3px] rounded-[20px] text-[10px] font-semibold tracking-[0.06em] uppercase backdrop-blur-[8px]" style={{ background: `${meta.accent}22`, border: `1px solid ${meta.accent}40`, color: meta.accent }}>
                    {meta.label}
                  </div>
                  <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: meta.accent }} />
                </div>

                <div className="p-[16px] flex flex-col gap-[10px] flex-1">
                  <h3 className="text-[15px] font-semibold text-[color:var(--text-primary)] leading-[1.4] m-0">{item.title}</h3>
                  {item.summary && <p className="text-[13px] text-[color:var(--text-muted)] leading-[1.6] m-0">{item.summary.slice(0, 160)}{item.summary.length > 160 ? '…' : ''}</p>}

                  <div className="flex flex-wrap gap-[5px]">
                    {item.tags.map(t => (
                      <span key={t.id} className="flex items-center gap-[3px] px-[8px] py-[3px] bg-[var(--glass-border)] border border-[var(--glass-border)] rounded-[20px] text-[11px] text-[color:var(--text-muted)]"><Tag size={9} />{t.name}</span>
                    ))}
                    {(item.city || item.province) && (
                      <span className="flex items-center gap-[3px] px-[8px] py-[3px] bg-[rgba(56,189,248,0.08)] border border-[rgba(56,189,248,0.2)] rounded-[20px] text-[11px] text-[color:var(--accent-blue)]"><MapPin size={9} />
                        {item.city && item.province ? `${item.city}, ${item.province}` : item.city || item.province}
                      </span>
                    )}
                  </div>

                  <div className="h-px bg-[var(--border)] mx-[-16px]" />

                  <div className="flex gap-[6px] pt-[4px]">
                    <button className={actionBtnClass('blue')} onClick={() => window.open(item.sourceUrl, '_blank')}>
                      <ExternalLink size={12} /> View
                    </button>
                    {canEditTags && (
                      <button className={actionBtnClass('ghost')} onClick={() => setEditingFor(item.id)}>Tags</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Infinite-scroll sentinel + "loading more" indicator */}
      <div ref={sentinelRef} className="h-[1px]" />
      {loadingMore && (
        <div className="flex justify-center items-center pb-[40px] gap-[8px]">
          {[0,150,300].map(d => <div key={d} className="w-[6px] h-[6px] rounded-full bg-[var(--accent)]" style={{ animation:`bounce 1s ${d}ms infinite` }} />)}
        </div>
      )}

      {/* ── Tag edit modal ── */}
      {editingFor && (() => {
        const item = items.find(i => i.id === editingFor);
        if (!item) return null;
        return (
          <div className="fixed inset-0 bg-[rgba(0,0,0,0.85)] flex items-center justify-center z-50 p-[16px]" onClick={e => e.target === e.currentTarget && setEditingFor(null)}>
            <div className="bg-[var(--surface)] border border-[var(--glass-border)] rounded-[20px] p-[28px] w-full max-w-[420px] max-h-[80vh] flex flex-col gap-[16px]">
              <h3 className="text-[16px] font-semibold text-[color:var(--text-primary)] m-0">Edit Tags</h3>
              <div className="overflow-y-auto flex flex-col gap-[4px]">
                {tags.map(tag => {
                  const checked = item.tags.some(t => t.id === tag.id);
                  return (
                    <label key={tag.id} className="flex items-center gap-[12px] px-[14px] py-[10px] bg-[var(--surface-hover)] rounded-[10px] cursor-pointer transition-all duration-150">
                      <input type="checkbox" checked={checked} className="w-[15px] h-[15px] [accent-color:var(--accent)]" onChange={e => {
                        const next = e.target.checked ? [...item.tags, tag] : item.tags.filter(t => t.id !== tag.id);
                        updateTags(item.id, next);
                      }} />
                      <span className="text-[13px] text-[color:var(--text-secondary)]">{tag.name}</span>
                    </label>
                  );
                })}
              </div>
              <button className="p-[10px] bg-[var(--glass-border)] border border-[var(--glass-border)] rounded-[10px] text-[13px] font-medium text-[color:var(--text-secondary)] cursor-pointer w-full transition-all duration-150" onClick={() => setEditingFor(null)}>Close</button>
            </div>
          </div>
        );
      })()}

      <style>{`@keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-7px)} }`}</style>
    </div>
  );
};

export default ModernUpdateFeed;
